<script setup>
import { register, confirmEmail } from "@/services/ApiUrlService"
import { useRoute } from "vue-router"
import { useApiHandler } from "@/composables/usePostApi"
import { router } from "@/plugins/1.router"
import { useAuthStore } from "@/stores/authStore"
import { useCustomSwal } from "@/composables/usePopup"
import { codePhone } from "@/utils/code"

import FooterBlank from "@/layouts/components/FooterBlank.vue"

definePage({
  meta: {
    layout: "blank",
    public: true,
  },
})
const { showCustomSwal } = useCustomSwal()
const CURRENT_YEAR = import.meta.env.VITE_CURRENT_YEAR || 2026
const authStore = useAuthStore()
const { handleApiRequest, isLoading, error } = useApiHandler()
const route = useRoute()
const isEmail = !!route.query.email
const isToken = !!route.query.token

const text = ref(null)

const itemsVous = [
  {
    id: 1,
    name: $gettext("Le représentant d'un établissement d'enseignement supérieur / d'une formation"),
  },
  { id: 2, name: $gettext("Un recruteur / un RH") },
  { id: 3, name: $gettext("Un étudiant / un participant à une formation") },
]

const createPassword = async (obj) => {
  const response = await handleApiRequest(confirmEmail, obj, {
    loading: true,
    noPushMessage: true,
  })

  if (response) {
    if (response.result === "invalid token") {
      await showCustomSwal({
        html: `<h4 class="text-error mb-0">${$gettext(
          "Jeton non valide ou expiré"
        )}</h4>`,
        confirmButtonColor: "#716296",
        confirmButtonText: $gettext("Annuler"),
      })
      return
    }
    await showCustomSwal({
      html: `<h4 class="text-success mb-0">${$gettext(
        "Votre nouveau mot de passe a été enregistré avec succès."
      )}</h4>`,
      showCancelButton: true,
      showConfirmButton: false,
      cancelButtonColor: "#716296",
      cancelButtonText: $gettext("Annuler"),
      allowOutsideClick: false,
      timer: 5000,
      timerProgressBar: true,
      willClose: () => {
        authStore.loginByCreatePassword(response)
      },
    })
  } else {
    await showCustomSwal({
      html: `<h4 class="text-error">${$gettext(
        "Une erreur système s'est produite."
      )}</h4>`,
      showCancelButton: true,
      showConfirmButton: false,
      cancelButtonColor: "#716296",
      cancelButtonText: $gettext("Annuler"),
    })
  }
}

let timeValid = null
const registerUser = async (obj) => {
  if (obj.telephoneCode) {
    obj.telephoneCode = obj.telephoneCode.code
  }
  if (obj.mobileCode) {
    obj.mobileCode = obj.mobileCode.code
  }
  const response = await handleApiRequest(register, obj, {
    loading: true,
    noPushMessage: true,
  })

  if (response) {
    await showCustomSwal({
      // title: $gettext("Votre inscription a bien été enregistrée !"),
      html: `<h3 class="text-primary mb-3 text-center">${$gettext(
        "Votre inscription a bien été enregistrée !"
      )}</h3> <p class="text-black text-center mb-0">${$gettext(
        "Vous recevrez, dans les 48h, un e-mail avec vos identifiants de connexion."
      )}</p>
    <div style="margin: 10 auto;">
      <small class="text-error mt-3 text-center" style="font-weight: normal"><i>${$gettext(
        "Si vous ne recevez rien d’ici 48h00 : VÉRIFIEZ BIEN VOS SPAMS"
      )}</i></small>
      </div>
    
    `,
      showCancelButton: false,
      showConfirmButton: false,
      showCloseButton: true,
      allowOutsideClick: false,
      timer: 5000,
      timerProgressBar: true,
      willClose: () => {
        router.push({ name: "login" })
      },
    })
  } else {
    console.log(error.value)
    if (error.value === "Email already exists") {
      await showCustomSwal({
        html: `<p class="text-error mb-0">${$gettext(
          "L'e-mail existe déjà"
        )}</p>`,
        showCancelButton: false,
        showConfirmButton: false,
        showCloseButton: true,
      })
    } else {
      await showCustomSwal({
        html: `<p class="text-error mb-0">${$gettext(
          "Une erreur système s'est produite."
        )}</p>`,
        showCancelButton: false,
        showConfirmButton: false,
        showCloseButton: true,
      })
    }
  }
}

const onSubmit = async (obj) => {
  if (isEmail && isToken) {
    createPassword(obj)
  } else {
    registerUser(obj)
  }
}

const redirectLogin = () => {
  router.push({ name: "login" })
}
</script>

<template>
  <div class="register">
    <div class="register__banner">
      <div class="register__banner-title">
        <img
          src="@/assets/images/Logo_EEA_Ocre.png"
          width="148"
          height="84"
          loading="lazy"
          alt="logo"
          cover
          @click="redirectLogin"
          class="pointer"
        />
        <h1>{{ $gettext("Enquêtes France") }} {{ CURRENT_YEAR }}</h1>
      </div>
    </div>

    <div class="register__form" v-if="!isEmail && !isToken">
      <FormUser
        :listVous="itemsVous"
        :listTele="codePhone"
        :loading="isLoading"
        @update:modelValue="onSubmit($event)"
      />
    </div>

    <div class="register__license" v-if="!isEmail && !isToken">
      <p>
        {{ $gettext("En validant ces informations, vous acceptez notre") }}
        <a
          href="https://eduniversal.com/privacy-policy"
          target="_blank"
        >
          {{ $gettext("politique de confidentialité") }}
        </a>
      </p>
      <p>
        {{ $gettext("Vous avez déjà un compte ?") }}
        <span @click="redirectLogin">{{ $gettext("S'identifier") }}</span>
      </p>
    </div>
    <div class="register__form" v-if="isEmail && isToken">
      <FormPassword
        :loading="isLoading"
        :text="text"
        @update:modelValue="onSubmit($event)"
      />
    </div>

    <FooterBlank />
  </div>
</template>

<style lang="scss">
@import "@/assets/styles/pages/register.scss";
</style>
